import { create } from "zustand";
import type { SceneTransform, TimelineClip, TimelineTrack, Vector3Value } from "@/types/scene";

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
const snapValue = (value: number, snap: number) => Number((Math.round(value / snap) * snap).toFixed(4));
const clipDuration = (name: string) => /idle/i.test(name) ? 2 : /chop|dig|work/i.test(name) ? 3 : 2.5;

interface TimelineState {
  currentTime: number;
  duration: number;
  tracks: TimelineTrack[];
  playing: boolean;
  playbackSpeed: number;
  loop: boolean;
  snap: number;
  pixelsPerSecond: number;
  selectedClipId: string | null;
  revision: number;
  setCurrentTime: (time: number) => void;
  setPlaying: (playing: boolean) => void;
  togglePlaying: () => void;
  stop: () => void;
  restart: () => void;
  tick: (delta: number) => void;
  setPlaybackSpeed: (speed: number) => void;
  toggleLoop: () => void;
  setSnap: (snap: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  selectClip: (id: string | null) => void;
  addAnimationClip: (objectId: string, name: string) => string;
  addWalkToAction: (objectId: string, targetId: string, from: Vector3Value, to: Vector3Value, duration: number, autoRotate: boolean, stoppingDistance: number) => string[];
  addCameraMove: (objectId: string, from: Pick<SceneTransform, "position" | "rotation">, to: Pick<SceneTransform, "position" | "rotation">, duration: number) => string;
  moveClip: (clipId: string, startTime: number) => void;
  resizeClip: (clipId: string, duration: number) => void;
  deleteClip: (clipId: string) => void;
  duplicateClip: (clipId: string) => string | null;
  removeObjectTracks: (objectId: string) => void;
  replaceTimeline: (tracks: TimelineTrack[], duration?: number) => void;
  clearTimeline: () => void;
}

export const useTimelineStore = create<TimelineState>((set, get) => ({
  currentTime: 0,
  duration: 15,
  tracks: [],
  playing: false,
  playbackSpeed: 1,
  loop: false,
  snap: .1,
  pixelsPerSecond: 70,
  selectedClipId: null,
  revision: 0,
  setCurrentTime: (time) => set((state) => ({ currentTime: clamp(time, 0, state.duration), playing: false })),
  setPlaying: (playing) => set({ playing }),
  togglePlaying: () => set((state) => ({ playing: !state.playing })),
  stop: () => set({ playing: false, currentTime: 0 }),
  restart: () => set({ playing: true, currentTime: 0 }),
  tick: (delta) => set((state) => {
    const next = state.currentTime + delta * state.playbackSpeed;
    if (next < state.duration) return { currentTime: next };
    return state.loop ? { currentTime: 0 } : { currentTime: state.duration, playing: false };
  }),
  setPlaybackSpeed: (playbackSpeed) => set({ playbackSpeed }),
  toggleLoop: () => set((state) => ({ loop: !state.loop })),
  setSnap: (snap) => set({ snap }),
  zoomIn: () => set((state) => ({ pixelsPerSecond: Math.min(140, state.pixelsPerSecond + 15) })),
  zoomOut: () => set((state) => ({ pixelsPerSecond: Math.max(35, state.pixelsPerSecond - 15) })),
  selectClip: (selectedClipId) => set({ selectedClipId }),
  addAnimationClip: (objectId, name) => {
    const state = get();
    const id = `clip_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const clip: TimelineClip = { id, name, startTime: snapValue(state.currentTime, state.snap), duration: Math.min(clipDuration(name), state.duration - state.currentTime), data: { animation: name, loop: !/fall|jump/i.test(name) } };
    const existing = state.tracks.find((track) => track.objectId === objectId && track.trackType === "animation");
    const tracks = existing ? state.tracks.map((track) => track.id === existing.id ? { ...track, clips: [...track.clips, clip] } : track) : [...state.tracks, { id: `track_${objectId}_animation`, objectId, trackType: "animation" as const, clips: [clip] }];
    set({ tracks, selectedClipId: id, revision: state.revision + 1 });
    return id;
  },
  addWalkToAction: (objectId, targetId, from, to, requestedDuration, autoRotate, stoppingDistance) => {
    const state = get();
    const startTime = snapValue(state.currentTime, state.snap);
    const duration = Math.max(state.snap, Math.min(requestedDuration, state.duration - startTime));
    const suffix = `${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const animationClip: TimelineClip = { id: `clip_walk_${suffix}`, name: "Walk", startTime, duration, data: { animation: "Walk", loop: true, linkedActionId: suffix } };
    const movementClip: TimelineClip = { id: `clip_move_${suffix}`, name: "Move to target", startTime, duration, data: { from, to, autoRotate, targetId, stoppingDistance, linkedActionId: suffix } };
    const upsert = (tracks: TimelineTrack[], trackType: "animation" | "movement", clip: TimelineClip) => {
      const existing = tracks.find((track) => track.objectId === objectId && track.trackType === trackType);
      return existing ? tracks.map((track) => track.id === existing.id ? { ...track, clips: [...track.clips, clip] } : track) : [...tracks, { id: `track_${objectId}_${trackType}`, objectId, trackType, clips: [clip] }];
    };
    const withAnimation = upsert(state.tracks, "animation", animationClip);
    set({ tracks: upsert(withAnimation, "movement", movementClip), selectedClipId: movementClip.id, revision: state.revision + 1 });
    return [animationClip.id, movementClip.id];
  },
  addCameraMove: (objectId, from, to, requestedDuration) => {
    const state = get();
    const startTime = snapValue(state.currentTime, state.snap);
    const duration = Math.max(state.snap, Math.min(requestedDuration, state.duration - startTime));
    const id = `clip_camera_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const clip: TimelineClip = { id, name: "Camera Move", startTime, duration, data: { from, to } };
    const existing = state.tracks.find((track) => track.objectId === objectId && track.trackType === "camera");
    const tracks = existing ? state.tracks.map((track) => track.id === existing.id ? { ...track, clips: [...track.clips, clip] } : track) : [...state.tracks, { id: `track_${objectId}_camera`, objectId, trackType: "camera" as const, clips: [clip] }];
    set({ tracks, selectedClipId: id, revision: state.revision + 1 });
    return id;
  },
  moveClip: (clipId, startTime) => set((state) => ({ tracks: state.tracks.map((track) => ({ ...track, clips: track.clips.map((clip) => clip.id === clipId ? { ...clip, startTime: clamp(snapValue(startTime, state.snap), 0, state.duration - clip.duration) } : clip) })), revision: state.revision + 1 })),
  resizeClip: (clipId, duration) => set((state) => ({ tracks: state.tracks.map((track) => ({ ...track, clips: track.clips.map((clip) => clip.id === clipId ? { ...clip, duration: clamp(snapValue(duration, state.snap), state.snap, state.duration - clip.startTime) } : clip) })), revision: state.revision + 1 })),
  deleteClip: (clipId) => set((state) => ({ tracks: state.tracks.map((track) => ({ ...track, clips: track.clips.filter((clip) => clip.id !== clipId) })).filter((track) => track.clips.length), selectedClipId: state.selectedClipId === clipId ? null : state.selectedClipId, revision: state.revision + 1 })),
  duplicateClip: (clipId) => {
    const state = get();
    const track = state.tracks.find((item) => item.clips.some((clip) => clip.id === clipId));
    const source = track?.clips.find((clip) => clip.id === clipId);
    if (!track || !source) return null;
    const id = `clip_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const copy = { ...source, id, startTime: clamp(source.startTime + source.duration, 0, state.duration - source.duration) };
    set({ tracks: state.tracks.map((item) => item.id === track.id ? { ...item, clips: [...item.clips, copy] } : item), selectedClipId: id, revision: state.revision + 1 });
    return id;
  },
  removeObjectTracks: (objectId) => set((state) => ({ tracks: state.tracks.filter((track) => track.objectId !== objectId), selectedClipId: state.tracks.some((track) => track.objectId === objectId && track.clips.some((clip) => clip.id === state.selectedClipId)) ? null : state.selectedClipId, revision: state.revision + 1 })),
  replaceTimeline: (tracks, duration = 15) => set((state) => ({ tracks: structuredClone(tracks), duration, currentTime: 0, playing: false, selectedClipId: null, revision: state.revision + 1 })),
  clearTimeline: () => set((state) => ({ tracks: [], currentTime: 0, playing: false, selectedClipId: null, revision: state.revision + 1 })),
}));
